import { apiRequest, USE_MOCKS } from './client'
import type { ConfigProfile, UpdateConfigRequest, UpdateConfigResponse } from '@/types'

// --- Lightweight in-memory mocks for config ---
const mockConfigStore: Record<string, { revision: number; config: Record<string, unknown> }> = {
  '__global__': {
    revision: 3,
    config: {
      'upload.remote_base': '/nas/cctv',
      'recording.fps': 15,
      'recording.chunk_size_frames': 512,
      'disk.free_threshold_gb': 20,
      'upload.bandwidth_limit_mbps': null,
      'upload.max_retry': 3,
      'analysis_service.process_name': 'analysis_service',
    },
  },
}

function mockGet(scope: string, scopeKey: string): ConfigProfile {
  const key = scope === 'global' ? '__global__' : `${scope}:${scopeKey}`
  const stored = mockConfigStore[key] ?? { revision: 1, config: {} }
  return { scope, scope_key: scopeKey, revision: stored.revision, config: stored.config }
}

function mockUpdate(scope: string, scopeKey: string, body: UpdateConfigRequest): UpdateConfigResponse {
  const key = scope === 'global' ? '__global__' : `${scope}:${scopeKey}`
  const prev = mockConfigStore[key] ?? { revision: 0, config: {} }
  mockConfigStore[key] = { revision: prev.revision + 1, config: body.config }
  return { scope, scope_key: scopeKey, revision: prev.revision + 1 }
}
// --- End mocks ---

export async function getNodeConfig(nodeId: string): Promise<ConfigProfile> {
  if (USE_MOCKS) return mockGet('node', nodeId)
  return apiRequest<ConfigProfile>(`/api/nodes/${nodeId}/config`)
}

export async function updateNodeConfig(
  nodeId: string,
  body: UpdateConfigRequest,
): Promise<UpdateConfigResponse> {
  if (USE_MOCKS) return mockUpdate('node', nodeId, body)
  return apiRequest<UpdateConfigResponse>(`/api/nodes/${nodeId}/config`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export async function getSiteConfig(site: string): Promise<ConfigProfile> {
  if (USE_MOCKS) return mockGet('site', site)
  return apiRequest<ConfigProfile>(`/api/sites/${site}/config`)
}

export async function updateSiteConfig(
  site: string,
  body: UpdateConfigRequest,
): Promise<UpdateConfigResponse> {
  if (USE_MOCKS) return mockUpdate('site', site, body)
  return apiRequest<UpdateConfigResponse>(`/api/sites/${site}/config`, {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}

export async function getDefaultConfig(): Promise<ConfigProfile> {
  if (USE_MOCKS) return mockGet('global', 'global')
  return apiRequest<ConfigProfile>('/api/config/defaults')
}

export async function updateDefaultConfig(
  body: UpdateConfigRequest,
): Promise<UpdateConfigResponse> {
  if (USE_MOCKS) return mockUpdate('global', 'global', body)
  return apiRequest<UpdateConfigResponse>('/api/config/defaults', {
    method: 'PUT',
    body: JSON.stringify(body),
  })
}
